<template>
  <d-container fluid class="main-content-container px-4">
    <!-- Page Header -->
    <d-row no-gutters class="page-header py-4">
      <d-col col sm="6" class="text-center text-sm-left mb-4 mb-sm-0">
        <span class="text-uppercase page-subtitle">{{ $t('ui.inquiries') }}</span>
        <h3 class="page-title">
          <template v-if="isViewMode">{{ $t('inquiries.inquiriy') }}</template>
          <template v-else>{{ $t('inquiries.addInquiry') }}</template>
        </h3>
      </d-col>

      <d-col col sm="6" class="text-right text-sm-right d-flex justify-content-end align-items-end">
        <d-button
          theme="secondary"
          class="mr-2"
          @click.prevent="$router.push('/zapytania')"
          :disabled="submitInProgress">
          <i class="material-icons">arrow_back</i>
          {{ $t('ui.backToList') }}
        </d-button>

        <d-button
          v-if="!isViewMode"
          theme="success"
          @click.prevent="validateForm"
          :disabled="submitInProgress">
          <i class="material-icons">add</i>
          {{ $t('inquiries.sendInquiry') }}
        </d-button>

        <!--<d-button
          v-if="!isViewMode"
          theme="success"
          @click.prevent="beforeAddOrder"
          class="ml-2"
          :disabled="submitInProgress">
          <i class="material-icons">add</i>
          {{ $t('inquiries.sendOrder') }}
        </d-button>-->

        <d-button
          v-if="requestData && requestData.status === 'SENT_TO_CUSTOMER'"
          theme="success"
          @click.prevent="acceptOffer">
          <i class="material-icons">check_circle</i>
          {{ $t('inquiries.acceptOffer') }}
        </d-button>
      </d-col>
    </d-row>

    <d-alert
      v-if="isViewMode"
      show
      theme="warning">
      <strong>{{ $t('ui.warning') }}</strong> {{ $t('inquiries.contactUsIfYouNeedToChangeInquiry') }}
    </d-alert>

    <!-- Form -->
    <d-form>
      <validation-observer ref="request-form" tag="form">
        <d-card class="mb-5">
          <d-tabs
            v-if="!loadError"
            v-sticky-tabs
            card
            :nav-class="allDataLoaded ? validationResults : ''">

            <!-- Zapytanie -->
            <d-tab
              :title="$t('ui.inquiry')"
              active>
              <request-creator
                v-if="allDataLoaded"
                ref="request-creator"
                :readOnly="isViewMode"
                :products="products"
                :currencyID="allDataLoaded ? customer.currency_id : 0"
                :currencies="currencies"
                :services="services"
                :submittedFiles="requestData && requestData.files"
                :productsDiscount="allDataLoaded ? parseFloat(customer.discount_products) : 0"
                :servicesGroups="servicesGroups"
                :tagCategories="tagCategories" />
            </d-tab>

            <!-- Zamówienie -->
            <d-tab
              v-if="requestData && requestData.status === 'SENT_TO_CUSTOMER'"
              :title="$t('ui.offer')">
              <order-creator
                ref="order-creator"
                :title="$t('ui.offer')"
                :titleLabel="$t('inquiries.offerDative')"
                :readOnly="true"
                :products="products"
                :services="services"
                :currencyId="requestData.currency_id"
                :selectedCurrencySymbol="requestData.currency_code"
                :shipperPrice="requestData.shipper_price"
                :shipperAdditionalPrice="requestData.shipper_additional_price"
                :includeSetupPrice="requestData.include_setup_prices"
                :customerProductsDiscount="requestData.discount_products"
                :customerServicesDiscount="requestData.discount_services"
                :files="requestData && requestData.files"
                :status="requestData.status"
                :orderSetUpInProgress="orderSetUpInProgress"
                :warnings="[]"
                :collectAlert="false"
                :collectWarning="false" />
            </d-tab>

            <!-- Dane ogólne -->
            <d-tab :title="$t('ui.generalInfo')">
              <h4 class="mb-3 pb-3 border-bottom d-flex">
                {{ $t('ui.generalInfo') }}
              </h4>

              <d-row>
                <d-col cols="6">
                  <order-select-contact
                    v-if="allDataLoaded"
                    :readOnly="isViewMode"
                    ref="request-contact"
                    :customer-id="customerID" />
                </d-col>

                <d-col cols="6">
                  <order-select-address
                    v-if="allDataLoaded"
                    :readOnly="isViewMode"
                    ref="request-address"
                    :customer-id="customerID" />
                </d-col>
              </d-row>

              <hr>

              <d-row>
                <d-col cols="3" md="3" lg="3">
                  <label>
                    {{ $t('inquiries.needExecutionTill') }}:
                  </label>

                  <d-datepicker
                    v-if="!isViewMode"
                    v-model="realization_date"
                    :monday-first="true"
                    format="yyyy-MM-dd"
                    :placeholder="$t('ui.chooseDate')"
                    :language="translationsForDatePicker" />

                  <p
                    v-if="isViewMode"
                    class="text-primary">
                    {{ requestRealizationDate }}
                  </p>
                </d-col>
              </d-row>

              <hr>

              <div class="form-group">
                <label for="description">
                  {{ $t('inquiries.orderAdditionalInfo') }}:
                </label>

                <d-form-textarea
                  v-if="!isViewMode"
                  v-model="customer_description"
                  :value.prop="customer_description"
                  :placeholder="$t('inquiries.addComments')"
                  :rows="6" />

                <pre
                  v-if="isViewMode"
                  class="customer-desc">
                  <template v-if="requestData.customer_description">{{ requestData.customer_description }}</template><template v-else>{{ $t('inquiries.noComments') }}&hellip;</template>
                </pre>
              </div>

              <order-files
                ref="customer-request-files"
                :orderID="requestData ? requestData.id : 0"
                :orderFiles="requestData ? requestData.files : []"
                :isCustomerRequest="true"
                :readOnly="isViewMode" />
            </d-tab>
          </d-tabs>

          <div v-if="!allDataLoaded" class="pb-3 pt-3 text-center overlay-loading">
            {{ $t('ui.dataLoading') }}&hellip;
          </div>

          <d-alert
            v-if="loadError"
            show
            theme="warning">
            {{ $t('ui.dataLoadingFailed') }} <a href="javascript:window.location.reload();" class="alert-link">{{ $t('ui.refreshPage') }}</a> {{ $t('ui.toTryAgain') }}
          </d-alert>
        </d-card>
      </validation-observer>
    </d-form>

    <customer-contact-person-new-popup
      v-if="showContactPersonAddPopup"
      :customerID="customerID" />

    <customer-address-new-popup
      v-if="showAddressAddPopup"
      :countries="countries"
      :customerID="customerID" />
  </d-container>
</template>

<script>
import CustomerContactPersonNewPopup from './../customers/CustomerContactPersonNewPopup.vue';
import CustomerAddressNewPopup from './../customers/CustomerAddressNewPopup.vue';
import CustomerPanel from './../../mixins/CustomerPanel.vue';
import datepickerTranslations from './../../data/datepicker-translations.js';
import FormUtils from './../../utils/FormUtils.js';
import OrderCreator from './OrderCreator.vue';
import OrderFiles from './OrderFiles.vue';
import OrderHistory from './OrderHistory.vue';
import OrderSelectAddress from './OrderSelectAddress.vue';
import OrderSelectContact from './OrderSelectContact.vue';
import RequestCreator from './RequestCreator.vue';
import Vue from 'vue';
import getPrefferedLanguage from './../../utils/i18nUtils.js';

export default {
  name: 'request-customer-view',
  props: [
    'requestData'
  ],
  mixins: [
    CustomerPanel
  ],
  components: {
    'customer-contact-person-new-popup': CustomerContactPersonNewPopup,
    'customer-address-new-popup': CustomerAddressNewPopup,
    'order-creator': OrderCreator,
    'order-files': OrderFiles,
    'order-history': OrderHistory,
    'order-select-address': OrderSelectAddress,
    'order-select-contact': OrderSelectContact,
    'request-creator': RequestCreator
  },
  computed: {
    customerID () {
      return parseInt(this.$store.getters.getCustomerID, 10);
    },
    isViewMode () {
      return !!this.$route.params.id;
    },
    allDataLoaded () {
      return this.loadedData.join(',') === 'countries,currencies,customer,products,services,services-groups,tag-categories';
    },
    validationResults () {
      let errors = this.$refs['request-form'].errors;
      let allKeys = Object.keys(errors);
      let errorKeys = [];
      let errorTabs = [];
      let results = [
        ['error-1', ['contact_id', 'address_id']],
        ['error-2', ['product-id-', 'product-', 'product-amount-', 'service-id-', 'service-price', 'global-service-id-', 'global-service-price']]
      ];

      for (let i = 0; i < allKeys.length; i++) {
        if (errors[allKeys[i]].length) {
          errorKeys.push(allKeys[i]);
        }
      }

      errorKeys = errorKeys.join(',');

      for (let i = 0; i < results.length; i++) {
        for (let j = 0; j < results[i][1].length; j++) {
          let key = results[i][1][j];

          if (errorKeys.indexOf(key) > -1) {
            errorTabs.push(results[i][0]);
            break;
          }
        }
      }

      return errorTabs.join(' ');
    },
    translationsForDatePicker () {
      return datepickerTranslations[getPrefferedLanguage()];
    },
    requestRealizationDate () {
      if (this.requestData.realization_date) {
        return [
          this.requestData.realization_date.getFullYear(),
          this.requestData.realization_date.getMonth() + 1,
          this.requestData.realization_date.getDate()
        ].join('-');
      }

      return this.$t('inquiries.noDateSelected');
    }
  },
  watch: {
    loadedCountries () {
      this.$bus.$emit('request-data-fetch-success', 'countries');
    },
    loadedCurrencies () {
      this.$bus.$emit('request-data-fetch-success', 'currencies');
    },
    loadedProducts () {
      this.$bus.$emit('request-data-fetch-success', 'products');
    },
    loadedServices () {
      this.$bus.$emit('request-data-fetch-success', 'services');
    },
    loadedCustomer () {
      this.$bus.$emit('request-data-fetch-success', 'customer');
    },
    loadedServicesGroups () {
      this.$bus.$emit('request-data-fetch-success', 'services-groups');
    },
    loadedTagCategories () {
      this.$bus.$emit('request-data-fetch-success', 'tag-categories');
    },
    allDataLoaded (newValue) {
      if (newValue && this.isViewMode) {
        this.setUpOrder();
      }
    }
  },
  data() {
    return {
      realization_date: null,
      customer_description: '',
      statuses: [],
      products: [],
      services: [],
      servicesGroups: [],
      tagCategories: [],
      customer: null,
      // data dynamically loaded
      countries: [],
      currencies: [],
      // temporary data
      selectedCustomer: null,
      selectedAddressData: null,
      selectedPersonData: null,
      // ui flags
      showContactPersonAddPopup: false,
      showAddressAddPopup: false,
      missingProductAmount: 0,
      // flags
      loadError: false,
      loadedData: [],
      loadedCountries: false,
      loadedCurrencies: false,
      loadedCustomer: false,
      loadedProducts: false,
      loadedServices: false,
      loadedTagCategories: false,
      loadedServicesGroups: false,
      orderSetUpInProgress: false,
      submitInProgress: false
    };
  },
  mounted () {
    this.orderSetUpInProgress = true;
    this.loadAdditionalData();
    this.$bus.$on('request-data-fetch-success', this.dataFetchSuccess);
    this.$bus.$on('customer-add-contact-person-popup-close', this.closeContactPersonAddPopup);
    this.$bus.$on('customer-add-address-popup-close', this.closeAddressAddPopup);
    this.$bus.$on('order-show-add-contact-person-popup', this.showContactPersonNewPopup);
    this.$bus.$on('order-show-add-address-popup', this.showAddressNewPopup);
  },
  methods: {
    validateForm () {
      FormUtils.validate(this.$refs['request-form'], this.submitForm);
    },
    loadAdditionalData () {
      FormUtils.loadAdditionalData(this, {
        endpoint: this.getApiEndpoint('/customers/item'),
        loadedKey: 'loadedCustomer',
        outputKey: 'customer',
        errorKey: 'loadError'
      });

      FormUtils.loadAdditionalData(this, {
        method: 'get',
        endpoint: this.getApiEndpoint('/countries/items'),
        loadedKey: 'loadedCountries',
        outputKey: 'countries',
        errorKey: 'loadError'
      });

      FormUtils.loadAdditionalData(this, {
        method: 'get',
        endpoint: this.getApiEndpoint('/currencies/items'),
        loadedKey: 'loadedCurrencies',
        outputKey: 'currencies',
        errorKey: 'loadError'
      });

      FormUtils.loadAdditionalData(this, {
        method: 'get',
        endpoint: this.getApiEndpoint('/products/all-items'),
        loadedKey: 'loadedProducts',
        outputKey: 'products',
        errorKey: 'loadError'
      });

      FormUtils.loadAdditionalData(this, {
        method: 'get',
        endpoint: this.getApiEndpoint('/services/all-items'),
        loadedKey: 'loadedServices',
        outputKey: 'services',
        errorKey: 'loadError'
      });

      FormUtils.loadAdditionalData(this, {
        method: 'get',
        endpoint: this.getApiEndpoint('/services/groups/all-items'),
        loadedKey: 'loadedServicesGroups',
        outputKey: 'servicesGroups',
        errorKey: 'loadError'
      });

      FormUtils.loadAdditionalData(this, {
        endpoint: this.getApiEndpoint('/tags/categories/items'),
        loadedKey: 'loadedTagCategories',
        outputKey: 'tagCategories',
        errorKey: 'loadError',
        params: {
          with: ['tags']
        }
      });
    },
    submitForm () {
      let fileFields = false;
      let dataToSend = this.prepareFormData('inquiry');

      if (this.isViewMode) {
        return;
      }

      this.submitInProgress = true;

      if (this.$refs['request-creator'] && this.$refs['request-creator'].files && this.$refs['request-creator'].files.filter(file => file !== null).length) {
        dataToSend.files = this.$refs['request-creator'].files.filter(file => file !== null);
        fileFields = [
          dataToSend.files
        ];
      }

      FormUtils.submit({
        $http: this.$http,
        endpoint: this.getApiEndpoint('/orders/store-inquiry'),
        formData: dataToSend,
        successTitle: this.$t('inquiries.inquirySaveSccessTitle'),
        successText: this.$t('inquiries.inquirySaveSuccessText'),
        errorTitle: this.$t('ui.errorOccured'),
        errorText: this.$t('inquiries.inquirySaveErrorText'),
        successAction: () => {
          this.$router.push('/zapytania');
          this.submitInProgress = true;
        },
        failAction: () => {
          this.submitInProgress = false;
        }
      }, fileFields);
    },
    prepareFormData (type) {
      let formData = {
        contact_id: this.$refs['request-contact'].contact_id,
        address_id: this.$refs['request-address'].address_id,
        offer_products: this.$refs['request-creator'].prepareProductsData(),
        offer_services_groups: this.$refs['request-creator'].prepareServicesGroupsData(),
        customer_description: this.customer_description,
        realization_date: this.realization_date ? [this.realization_date.getFullYear(), this.realization_date.getMonth() + 1, this.realization_date.getDate()].map(n => n < 10 ? '0' + n : n).join('-') : null
      };

      if (type === 'order') {
        formData.is_inquiry = 0;
        formData.products = this.$refs['request-creator'].prepareProductsData();
      }

      if (!this.isViewMode && this.$refs['customer-request-files'] && this.$refs['customer-request-files'].token) {
        formData.files_token = this.$refs['customer-request-files'].token;
      }

      return formData;
    },
    showContactPersonNewPopup () {
      this.showContactPersonAddPopup = true;
    },
    closeContactPersonAddPopup (contactID) {
      this.showContactPersonAddPopup = false;

      if (contactID) {
        this.$bus.$emit('order-select-contact-fetch-data', contactID);
      }
    },
    showAddressNewPopup () {
      this.showAddressAddPopup = true;
    },
    closeAddressAddPopup (addressID) {
      this.showAddressAddPopup = false;

      if (addressID) {
        this.$bus.$emit('order-select-address-fetch-data', addressID);
      }
    },
    dataFetchSuccess (dataKey) {
      if (this.loadedData.indexOf(dataKey) === -1) {
        this.loadedData.push(dataKey);
        this.loadedData.sort();
      }
    },
    setUpOrder () {
      Vue.nextTick(() => {
        this.$refs['request-creator'].setUpProducts(this.requestData.offer_products);
        this.$refs['request-creator'].setUpServices(this.requestData.offer_services_groups);
        this.$refs['request-address'].setAddressID(this.requestData.address_id);
        this.$refs['request-contact'].setContactID(this.requestData.contact_id);

        if (this.$refs['order-creator']) {
          this.$refs['order-creator'].setUpProducts(this.requestData.products);
          this.$refs['order-creator'].setUpServices(this.requestData.services);
        }
      });
    },
    getServiceNameByID (id) {
      let foundedService = this.services.filter(service => service.id === parseInt(id, 10));

      if (foundedService[0]) {
        return [foundedService[0].group_name, foundedService[0].name].filter(item => !!item).join(' - ');
      }

      return '??';
    },
    acceptOffer () {
      FormUtils.submit({
        $http: this.$http,
        endpoint: this.getApiEndpoint('/orders/accept-offer/' + this.requestData.id),
        formData: {},
        successTitle: this.$t('inquiries.inquiryAcceptSuccessTitle'),
        successText: this.$t('inquiries.inquiryAcceptSuccessText'),
        errorTitle: this.$t('ui.errorOccured'),
        errorText: this.$t('inquiries.inquiryAcceptErrorText'),
        successAction: () => {
          this.$router.push('/zapytania')
        }
      });
    },
    beforeAddOrder () {
      FormUtils.validate(this.$refs['request-form'], this.tryAddOrder);
    },
    tryAddOrder () {
      if (!this.$refs['request-creator']) {
        Vue.swal({
          title: this.$t('inquiries.unexpectedErrorOccured'),
          html: this.$t('ui.tryAgain'),
          type: 'warning',
          confirmButtonText: this.$t('ui.ok'),
          buttonsStyling: true
        });
        return;
      }

      if (!this.$refs['request-creator'].productsData.length) {
        Vue.swal({
          title: this.$t('inquiries.caanotCreateOrder'),
          html: this.$t('inquiries.failedToAddProductsToOrder'),
          type: 'warning',
          confirmButtonText: this.$t('ui.ok'),
          buttonsStyling: true
        });
        return;
      }

      if (this.$refs['request-creator'].offerServicesGroups.length) {
        Vue.swal({
          title: this.$t('inquiries.caanotCreateOrder'),
          html: this.$t('inquiries.yourOrderContainsDecorationsSoSendInquiry'),
          type: 'warning',
          confirmButtonText: this.$t('ui.ok'),
          buttonsStyling: true
        });
        return;
      }

      let productsWithTooLowAvailability = this.$refs['request-creator'].productsData.filter(product => {
        let qty = product.quantity;
        let foundedProduct = this.products.filter(productFromList => productFromList.id === product.product_id);
        let foundedProductAvailability = 0;

        if (foundedProduct && foundedProduct.length) {
          if (foundedProduct[0].availability) {
            foundedProductAvailability = parseInt(foundedProduct[0].availability, 10);
          }
        }

        return qty > foundedProductAvailability;
      });

      if (productsWithTooLowAvailability.length) {
        Vue.swal({
          title: this.$t('inquiries.caanotCreateOrder'),
          html: this.$t('inquiries.someProductAreUnavailable'),
          type: 'warning',
          confirmButtonText: this.$t('ui.ok'),
          buttonsStyling: true
        });
        return;
      }

      let dataToSend = this.prepareFormData('order');

      if (this.isViewMode) {
        return;
      }

      FormUtils.submit({
        $http: this.$http,
        endpoint: this.getApiEndpoint('/orders/store-inquiry'),
        formData: dataToSend,
        successTitle: this.$t('purchase.editOrderSuccessTitle'),
        successText: this.$t('purchase.yourOrderSaveSuccessText'),
        errorTitle: this.$t('ui.errorOccured'),
        errorText: this.$t('purchase.addOrderErrorText'),
        successAction: () => {
          this.$router.push('/zamowienia')
        }
      });
    }
  },
  beforeDestroy () {
    this.$bus.$off('request-data-fetch-success', this.dataFetchSuccess);
    this.$bus.$off('customer-add-contact-person-popup-close', this.closeContactPersonAddPopup);
    this.$bus.$off('customer-add-address-popup-close', this.closeAddressAddPopup);
    this.$bus.$off('order-show-add-contact-person-popup', this.showContactPersonNewPopup);
    this.$bus.$off('order-show-add-address-popup', this.showAddressNewPopup);
  }
};
</script>

<style lang="scss" scoped>
.form-control.is-select {
  background-image: none!important;
  padding: 0!important;
}

pre {
  background: #f5f5f5;
  border: 1px solid #e5e5e5;
  border-radius: 5px;
  font-family: Arial, sans-serif;
  font-size: 14px;
  padding: 20px;
  white-space: pre-line;
}
</style>
